import type { ColumnSchema } from '../../api/datasets';
import type { DashboardBlock } from '../../api/dashboards';

/**
 * Turning the builder's choices into a block.
 *
 * The form collects intent — "a bar chart of revenue by product" — and this
 * turns it into the declarative definition the engine runs. Keeping it a pure
 * function means the hard part (which shape each chart type needs) is tested
 * without a browser, and the backend still re-validates everything: this is a
 * convenience, never an authority.
 */

export type BlockTypeKey =
  | 'kpi'
  | 'statistic'
  | 'line'
  | 'area'
  | 'bar'
  | 'pie'
  | 'donut'
  | 'scatter'
  | 'histogram'
  | 'box'
  | 'heatmap'
  | 'table'
  | 'text'
  | 'filter';

export type Aggregation =
  'sum' | 'avg' | 'min' | 'max' | 'count' | 'count_distinct' | 'median' | 'stddev';

export type Granularity = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface MeasureChoice {
  column: string;
  aggregation: Aggregation;
}

export interface BlockDraft {
  id: string;
  type: BlockTypeKey;
  title: string;
  description?: string;
  /** Columns to group by, in order. */
  groupBy: string[];
  /** For a date grouping. */
  granularity?: Granularity;
  /** For a histogram: how many buckets to cut the range into. */
  bins?: number;
  measures: MeasureChoice[];
  /** For a text block. */
  text?: string;
  /** For a box plot: the number whose spread is drawn. */
  boxColumn?: string;
  /** For a filter control: the column it narrows and how. */
  filterColumn?: string;
  filterOperator?: string;
  /** Block ids this filter drives. Empty means every block with a question. */
  targets?: string[];
  limit?: number;
  layout?: { x: number; y: number; w: number; h: number };
}

/** How many groupings and measures each type takes, for the form to enforce. */
export const BLOCK_SHAPES: Record<
  BlockTypeKey,
  { groupBy: [number, number]; measures: [number, number]; label: string }
> = {
  kpi: { groupBy: [0, 0], measures: [1, 1], label: 'KPI' },
  statistic: { groupBy: [0, 0], measures: [2, 2], label: 'Statistic' },
  line: { groupBy: [1, 2], measures: [1, 1], label: 'Line chart' },
  area: { groupBy: [1, 2], measures: [1, 1], label: 'Area chart' },
  bar: { groupBy: [1, 2], measures: [1, 1], label: 'Bar chart' },
  pie: { groupBy: [1, 1], measures: [1, 1], label: 'Pie chart' },
  donut: { groupBy: [1, 1], measures: [1, 1], label: 'Donut chart' },
  scatter: { groupBy: [1, 1], measures: [2, 2], label: 'Scatter plot' },
  histogram: { groupBy: [1, 1], measures: [1, 1], label: 'Histogram' },
  box: { groupBy: [1, 1], measures: [5, 5], label: 'Box plot' },
  heatmap: { groupBy: [2, 2], measures: [1, 1], label: 'Heatmap' },
  table: { groupBy: [0, 4], measures: [0, 8], label: 'Table' },
  text: { groupBy: [0, 0], measures: [0, 0], label: 'Text' },
  filter: { groupBy: [0, 0], measures: [0, 0], label: 'Filter' },
};

const DEFAULT_SIZE: Record<BlockTypeKey, { w: number; h: number }> = {
  kpi: { w: 3, h: 2 },
  statistic: { w: 3, h: 2 },
  line: { w: 6, h: 4 },
  area: { w: 6, h: 4 },
  bar: { w: 6, h: 4 },
  pie: { w: 4, h: 4 },
  donut: { w: 4, h: 4 },
  scatter: { w: 6, h: 4 },
  histogram: { w: 6, h: 4 },
  box: { w: 6, h: 4 },
  heatmap: { w: 6, h: 4 },
  table: { w: 12, h: 4 },
  text: { w: 12, h: 2 },
  filter: { w: 4, h: 2 },
};

const TEMPORAL = new Set(['date', 'datetime']);
const NUMERIC = new Set(['integer', 'float']);

export function isTemporal(column: ColumnSchema | undefined): boolean {
  return !!column && TEMPORAL.has(column.type);
}

export function isNumeric(column: ColumnSchema | undefined): boolean {
  return !!column && NUMERIC.has(column.type);
}

/** What a filter control can do to a column of this type. */
export const FILTER_OPERATORS_BY_TYPE: Record<string, string[]> = {
  text: ['in', 'eq', 'contains'],
  boolean: ['eq'],
  integer: ['in', 'eq', 'between', 'gte', 'lte'],
  float: ['eq', 'between', 'gte', 'lte'],
  date: ['between', 'gte', 'lte', 'eq'],
  datetime: ['between', 'gte', 'lte', 'eq'],
};

export function filterOperatorsFor(column: ColumnSchema | undefined): string[] {
  return column ? (FILTER_OPERATORS_BY_TYPE[column.type] ?? ['eq']) : ['in'];
}

/** Aggregations that make sense for a column of this type. */
export function aggregationsFor(column: ColumnSchema | undefined): Aggregation[] {
  if (!column) return ['count'];
  if (isNumeric(column)) {
    return ['sum', 'avg', 'min', 'max', 'median', 'stddev', 'count', 'count_distinct'];
  }
  return ['count', 'count_distinct', 'min', 'max'];
}

function alias(measure: MeasureChoice): string {
  return `${measure.aggregation}_${measure.column}`;
}

function measureField(measure: MeasureChoice): Record<string, unknown> {
  return { field: measure.column, aggregation: measure.aggregation, alias: alias(measure) };
}

function dimensionAlias(column: string, draft: BlockDraft, schema: ColumnSchema[]): string {
  const found = schema.find((entry) => entry.name === column);
  if (draft.type === 'histogram' && isNumeric(found)) return `${column}_bucket`;
  if (isTemporal(found) && draft.granularity) return `${column}_${draft.granularity}`;
  return column;
}

function dimensionField(
  column: string,
  draft: BlockDraft,
  schema: ColumnSchema[],
  binWidth: number | null,
): unknown {
  const found = schema.find((entry) => entry.name === column);
  if (draft.type === 'histogram' && isNumeric(found) && binWidth) {
    return { field: column, bin_width: binWidth };
  }
  if (isTemporal(found) && draft.granularity) {
    return { field: column, granularity: draft.granularity };
  }
  return column;
}

/**
 * The bucket width for a histogram.
 *
 * The width comes from the column's own range, which the profile measured, so
 * the buckets are a property of the data rather than a number someone typed.
 */
export function binWidthFor(
  column: ColumnSchema | undefined,
  bins: number,
  range?: { minimum: number | null; maximum: number | null },
): number | null {
  if (!column || !isNumeric(column) || bins < 1) return null;
  const minimum = range?.minimum ?? null;
  const maximum = range?.maximum ?? null;
  if (minimum === null || maximum === null || maximum <= minimum) return null;

  const raw = (maximum - minimum) / bins;
  // Round to a readable step: 1, 2, 5, 10, 20, 50 … so the axis reads in
  // numbers a person would have chosen.
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const steps = [1, 2, 5, 10];
  const step = steps.find((candidate) => raw <= candidate * magnitude) ?? 10;
  return Number((step * magnitude).toPrecision(6));
}

export interface DraftContext {
  schema: ColumnSchema[];
  /** Min and max per column, from the profile, for histogram buckets. */
  ranges?: Record<string, { minimum: number | null; maximum: number | null }>;
}

/** The five measures a box plot is made of, in the order it draws them. */
export const BOX_AGGREGATIONS: Aggregation[] = [
  'min',
  'p25' as Aggregation,
  'median',
  'p75' as Aggregation,
  'max',
];

export function boxMeasures(column: string): MeasureChoice[] {
  return BOX_AGGREGATIONS.map((aggregation) => ({ column, aggregation }));
}

/** A box plot's measures come from one column, in the order the plot reads them. */
export function withBoxMeasures(draft: BlockDraft): BlockDraft {
  if (draft.type !== 'box') return draft;
  return { ...draft, measures: boxMeasures(draft.boxColumn ?? '') };
}

/** Build the block a draft describes. The backend validates it again. */
export function toBlock(draft: BlockDraft, context: DraftContext): DashboardBlock {
  const size = DEFAULT_SIZE[draft.type];
  const layout = draft.layout ?? { x: 0, y: 0, ...size };

  if (draft.type === 'filter') {
    return {
      id: draft.id,
      type: 'filter',
      title: draft.title,
      description: draft.description ?? '',
      definition: {},
      display: {
        filter_field: draft.filterColumn ?? '',
        filter_operator: draft.filterOperator ?? 'in',
        targets: draft.targets ?? [],
      },
      layout,
    };
  }

  if (draft.type === 'text') {
    return {
      id: draft.id,
      type: 'text',
      title: draft.title,
      description: draft.description ?? '',
      definition: {},
      display: { text: draft.text ?? '' },
      layout,
    };
  }

  const schema = context.schema;
  const binWidth =
    draft.type === 'histogram'
      ? binWidthFor(
          schema.find((entry) => entry.name === draft.groupBy[0]),
          draft.bins ?? 20,
          context.ranges?.[draft.groupBy[0] ?? ''],
        )
      : null;

  const dimensions = draft.groupBy.map((column) => dimensionField(column, draft, schema, binWidth));
  const metrics = draft.measures.map(measureField);
  const aliases = draft.groupBy.map((column) => dimensionAlias(column, draft, schema));
  const measureAliases = draft.measures.map(alias);

  const definition: Record<string, unknown> = { limit: draft.limit ?? 100 };
  if (dimensions.length) definition.dimensions = dimensions;
  if (metrics.length) definition.metrics = metrics;

  // Time reads left to right; everything else reads biggest first.
  const firstColumn = schema.find((entry) => entry.name === draft.groupBy[0]);
  if (aliases.length && measureAliases.length) {
    const byTime = isTemporal(firstColumn) || draft.type === 'histogram';
    definition.sort = byTime
      ? [{ field: aliases[0], direction: 'asc' }]
      : [{ field: measureAliases[0], direction: 'desc' }];
  }

  const display: Record<string, unknown> = {};
  switch (draft.type) {
    case 'kpi':
      display.value_field = measureAliases[0];
      break;
    case 'statistic':
      display.value_field = measureAliases[0];
      display.secondary_field = measureAliases[1];
      break;
    case 'scatter':
      display.x_field = measureAliases[0];
      display.y_field = measureAliases[1];
      display.series_field = aliases[0];
      break;
    case 'heatmap':
      display.x_field = aliases[0];
      display.y_field = aliases[1];
      display.value_fields = [measureAliases[0]];
      break;
    case 'box':
      display.x_field = aliases[0];
      break;
    case 'table':
      break;
    default:
      display.x_field = aliases[0];
      display.value_fields = measureAliases.slice(0, 1);
      if (aliases[1]) display.series_field = aliases[1];
      break;
  }

  if (draft.groupBy[0] && draft.type !== 'scatter' && draft.type !== 'table') {
    const label = schema.find((entry) => entry.name === draft.groupBy[0])?.source_name;
    if (label) display.x_label = label;
  }

  return {
    id: draft.id,
    type: draft.type,
    title: draft.title,
    description: draft.description ?? '',
    definition,
    display: display as DashboardBlock['display'],
    layout,
  };
}
